import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBotProtection, TurnstileWidget } from '../components/BotProtection';
import '../styles/admin.scss';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { honeypotField, getSignals } = useBotProtection();
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReset, setTurnstileReset] = useState(0);
  const handleTurnstileToken = useCallback((token) => setTurnstileToken(token), []);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    if (!formData.password) {
      newErrors.password = 'Mot de passe est requis';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const result = await login(formData.email, formData.password, {
        ...getSignals(),
        turnstile_token: turnstileToken,
      });
      if (!result.success) {
        setTurnstileReset((n) => n + 1);
      }
      if (result.success) {
        // Redirect based on user role
        if (result.user?.role === 'client') {
          navigate('/my-orders');
        } else {
          navigate('/admin/dashboard');
        }
      } else {
        setErrors({ submit: result.error });
      }
    } catch (error) {
      setErrors({ submit: 'Une erreur est survenue. Veuillez réessayer.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-wrapper">
      <div className="admin-auth-page">
        <div className="auth-card">
          <div className="auth-logo">
            <Link to="/">
              <img src="/assets/images/logo/logo.png" alt="Groupe Délices" />
            </Link>
          </div>

          <h1 className="auth-title">Connexion Admin</h1>
          <p className="auth-subtitle">
            Accédez à votre espace d'administration
          </p>

          {errors.submit && (
            <div className="alert alert-danger" style={{
              padding: '0.875rem',
              background: '#fee2e2',
              color: '#dc2626',
              borderRadius: '12px',
              marginBottom: '1.5rem',
              fontSize: '0.9rem',
              textAlign: 'center'
            }}>
              {errors.submit}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ position: 'relative' }}>
            {honeypotField}
            <div className={`form-group ${errors.email ? 'has-error' : ''}`}>
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="admin@delice.com"
                disabled={loading}
              />
              {errors.email && (
                <span className="error-message">⚠ {errors.email}</span>
              )}
            </div>

            <div className={`form-group ${errors.password ? 'has-error' : ''}`}>
              <label htmlFor="password">Mot de passe</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                disabled={loading}
              />
              {errors.password && (
                <span className="error-message">⚠ {errors.password}</span>
              )}
            </div>

            <div className="form-options">
              <div className="remember-me">
                <input
                  type="checkbox"
                  id="remember"
                  name="remember"
                  checked={formData.remember}
                  onChange={handleChange}
                  disabled={loading}
                />
                <label htmlFor="remember">Se souvenir de moi</label>
              </div>
              <Link to="/admin/forgot-password">Mot de passe oublié?</Link>
            </div>

            <TurnstileWidget onToken={handleTurnstileToken} resetKey={turnstileReset} />

            <button 
              type="submit" 
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>

          <div className="auth-footer">
            Pas encore de compte? <Link to="/admin/register">S'inscrire</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
